// Offline action queue (docs/miniapp-m2-tz.md §3.8).
//
// When a task action is fired with no network, we stash it in localStorage,
// optimistic-UI keeps the screen consistent, and the queue is replayed in
// order the moment connectivity returns. A poison action (server keeps
// rejecting) is dropped after MAX_RETRY so it can't wedge the queue.
import { apiRequest } from './api/client'
import type { ApiError } from './api/types'

const KEY = 'pt:offline_queue'
const MAX_RETRY = 5

export interface QueuedAction {
  id: string
  endpoint: string
  method: 'POST'
  body: unknown
  retryCount: number
  createdAt: number
}

type Sub = (size: number, justQueued: boolean) => void

const subs = new Set<Sub>()
let draining = false

function read(): QueuedAction[] {
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as QueuedAction[]) : []
  } catch {
    return []
  }
}

function write(q: QueuedAction[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(q)) } catch { /* quota / private mode */ }
}

function notify(justQueued: boolean): void {
  const n = read().length
  subs.forEach((s) => { try { s(n, justQueued) } catch { /* listener errors are theirs */ } })
}

function uuid(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`
  }
}

function isNetworkError(e: unknown): boolean {
  const err = e as ApiError
  return err?.status === 0 || err?.reason === 'network'
}

export function getQueueSize(): number {
  return read().length
}

export function subscribeQueue(cb: Sub): () => void {
  subs.add(cb)
  return () => { subs.delete(cb) }
}

export function enqueue(endpoint: string, body: unknown): void {
  const q = read()
  q.push({ id: uuid(), endpoint, method: 'POST', body, retryCount: 0, createdAt: Date.now() })
  write(q)
  notify(true)
}

/** Replay queued actions in order. Stops on a network error (still offline);
 *  drops an action that keeps failing with a non-network error after MAX_RETRY.
 *  Guarded so overlapping triggers (online event + manual) don't double-send. */
export async function drainQueue(): Promise<void> {
  if (draining || !navigator.onLine) return
  draining = true
  try {
    while (true) {
      const q = read()
      if (q.length === 0) break
      const item = q[0]
      try {
        await apiRequest(item.endpoint, { method: item.method, body: item.body })
        const rest = read(); rest.shift(); write(rest)
        notify(false)
      } catch (e) {
        if (isNetworkError(e)) break // dropped offline again — try later
        // Server rejected (4xx/5xx). Count it; drop as poison past the cap.
        item.retryCount += 1
        const cur = read()
        if (item.retryCount >= MAX_RETRY) {
          cur.shift(); write(cur); notify(false)
        } else {
          cur[0] = item; write(cur)
          break // back off; next online tick retries
        }
      }
    }
  } finally {
    draining = false
  }
}

/** POST that survives offline: queues + resolves with `synthetic` when there's
 *  no network (or the request dies network-side), else does the real call. */
export async function postWithOfflineQueue<T>(
  endpoint: string,
  body: unknown,
  synthetic: T,
): Promise<T> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    enqueue(endpoint, body)
    return synthetic
  }
  try {
    return await apiRequest<T>(endpoint, { method: 'POST', body })
  } catch (e) {
    if (isNetworkError(e)) {
      enqueue(endpoint, body)
      return synthetic
    }
    throw e
  }
}

/** Wire the reconnect drain once, at startup. */
export function initOfflineSync(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('online', () => { void drainQueue() })
  void drainQueue() // catch a queue left over from a previous session
}
